/**
 * VADE (ödeme koşulu) — teklifte listeden seçilir, siparişte ödeme planına döner.
 *
 * Bir seçenek bir ya da birkaç PARÇADAN oluşur; her parça tutarın bir yüzdesidir
 * ve vadesi bir OLAYA göre başlar:
 *   PESIN        → sipariş günü
 *   SIPARIS      → sipariş günü + gün
 *   SEVK_ONCESI  → sipariş "Sevk" aşamasına geçirilmek istendiği gün
 *   FATURA       → fatura günü + gün (fatura kesilene kadar vade başlamaz)
 *
 * Etiket TEK YERDE üretilir (ekran, PDF, uyarı aynı cümleyi kursun diye).
 */
export const VADE_TABAN = ['PESIN', 'SIPARIS', 'SEVK_ONCESI', 'FATURA'];
export const VADE_TABAN_LABEL = {
    PESIN: 'Peşin', SIPARIS: 'Sipariş tarihinden', SEVK_ONCESI: 'Sevk öncesi', FATURA: 'Fatura tarihinden',
};
/** Gün sayısı bu tabanlarda anlam taşır; diğerlerinde yok sayılır. */
export const vadeGunlu = (taban) => taban === 'SIPARIS' || taban === 'FATURA';
const tek = (taban, gun = 0) => [{ oranPct: 100, taban, gun }];
/** Ayar (`vade_secenekleri`) hiç yazılmadıysa bunlar döner. */
export const VARSAYILAN_VADE_SECENEKLERI = [
    { id: 'pesin', ad: 'Peşin', parcalar: tek('PESIN') },
    ...[15, 30, 45, 90].map((g) => ({ id: `siparis-${g}`, ad: `Sipariş + ${g} gün`, parcalar: tek('SIPARIS', g) })),
    ...[15, 30, 45, 60, 90].map((g) => ({ id: `fatura-${g}`, ad: `Fatura + ${g} gün`, parcalar: tek('FATURA', g) })),
    { id: 'yari-pesin-yari-sevk', ad: '%50 peşin, %50 sevk öncesi',
        parcalar: [{ oranPct: 50, taban: 'PESIN', gun: 0 }, { oranPct: 50, taban: 'SEVK_ONCESI', gun: 0 }] },
    { id: 'yari-pesin-yari-fatura-30', ad: '%50 peşin, %50 fatura + 30 gün',
        parcalar: [{ oranPct: 50, taban: 'PESIN', gun: 0 }, { oranPct: 50, taban: 'FATURA', gun: 30 }] },
];
/** Tek parçanın adı: "Peşin", "Sipariş + 15 gün", "Fatura + 30 gün", "Sevk öncesi". */
export function vadeTabanEtiket(p) {
    switch (p.taban) {
        case 'PESIN': return 'Peşin';
        case 'SEVK_ONCESI': return 'Sevk öncesi';
        case 'SIPARIS': return p.gun > 0 ? `Sipariş + ${p.gun} gün` : 'Sipariş tarihinde';
        case 'FATURA': return p.gun > 0 ? `Fatura + ${p.gun} gün` : 'Fatura tarihinde';
    }
}
const oranYaz = (o) => `%${String(Math.round(o * 100) / 100).replace('.', ',')}`;
/**
 * Parça etiketi. Tek parçalı (%100) seçenekte oran yazılmaz: "Sipariş + 15 gün".
 * Çok parçalıda oran başa gelir: "%50 fatura + 30 gün".
 */
export function vadeParcaEtiket(p) {
    const ad = vadeTabanEtiket(p);
    return p.oranPct >= 100 ? ad : `${oranYaz(p.oranPct)} ${ad.toLocaleLowerCase('tr')}`;
}
/** Seçeneğin tamamı: "Sipariş + 15 gün" · "%50 peşin · %50 fatura + 30 gün". */
export function vadeEtiket(parcalar) {
    return parcalar.map(vadeParcaEtiket).join(' · ');
}
/**
 * Bir seçeneğin kusuru (yoksa null). Sunucu kaydı reddetmek, arayüz kaydetmeden
 * önce uyarmak için AYNI kuralı kullanır.
 */
export function vadeSecenekHatasi(s) {
    const v = s;
    if (!v || typeof v !== 'object')
        return 'Vade seçeneği okunamadı';
    const ad = typeof v.ad === 'string' ? v.ad.trim() : '';
    if (ad.length < 2)
        return 'Vade seçeneğinin adı gerekli';
    if (ad.length > 80)
        return `"${ad.slice(0, 20)}…": ad en çok 80 harf olabilir`;
    if (typeof v.id !== 'string' || !/^[a-z0-9-]{1,40}$/.test(v.id))
        return `"${ad}": kimlik geçersiz`;
    if (!Array.isArray(v.parcalar) || v.parcalar.length === 0)
        return `"${ad}": en az bir parça gerekli`;
    if (v.parcalar.length > 6)
        return `"${ad}": en çok 6 parça olabilir`;
    let toplam = 0;
    for (const p of v.parcalar) {
        if (!p || !VADE_TABAN.includes(p.taban))
            return `"${ad}": parçanın neye göre hesaplanacağı seçilmeli`;
        if (typeof p.oranPct !== 'number' || !Number.isFinite(p.oranPct) || p.oranPct <= 0 || p.oranPct > 100) {
            return `"${ad}": oran 0 ile 100 arasında olmalı`;
        }
        if (typeof p.gun !== 'number' || !Number.isInteger(p.gun) || p.gun < 0 || p.gun > 730) {
            return `"${ad}": gün 0 ile 730 arasında tam sayı olmalı`;
        }
        toplam += p.oranPct;
    }
    if (Math.abs(toplam - 100) > 0.001) {
        return `"${ad}": oranların toplamı %100 olmalı (şu an ${oranYaz(toplam)})`;
    }
    return null;
}
/** Liste denetimi: her seçenek geçerli, kimlikler tekil. */
export function vadeListeHatasi(liste) {
    if (!Array.isArray(liste) || liste.length === 0)
        return 'En az bir vade seçeneği olmalı';
    if (liste.length > 60)
        return 'En çok 60 vade seçeneği tanımlanabilir';
    const gorulen = new Set();
    for (const s of liste) {
        const h = vadeSecenekHatasi(s);
        if (h)
            return h;
        const id = s.id;
        if (gorulen.has(id))
            return `"${s.ad}": aynı kimlikte iki seçenek var`;
        gorulen.add(id);
    }
    return null;
}
/** Kayda girecek temiz kopya: fazladan alan taşımaz, gün anlamsızsa 0 olur. */
export function vadeTemizle(s) {
    return {
        id: s.id, ad: s.ad.trim(),
        parcalar: s.parcalar.map((p) => ({ oranPct: p.oranPct, taban: p.taban, gun: vadeGunlu(p.taban) ? p.gun : 0 })),
    };
}
/**
 * Tutarı parçalara böler. Her parça aşağı yuvarlanır, artan kuruş SON parçaya
 * eklenir — parçaların toplamı her zaman tutarın kendisidir.
 */
export function vadeTutarBol(tutarKurus, parcalar) {
    const paylar = parcalar.map((p) => Math.floor((tutarKurus * p.oranPct) / 100));
    if (paylar.length > 0)
        paylar[paylar.length - 1] += tutarKurus - paylar.reduce((t, x) => t + x, 0);
    return paylar;
}
